<template>
    <!-- Please refer to the official event handling guide https://v3.vuejs.org/guide/events.html#event-modifiers -->
    <!-- "self" only triggers handler if event.target is the element itself , i.e. not from a child element-->
    <!-- "self" will prevent the minimized notes from being clicked, since notes are children of this "note-container" div -->
    <div class="note-container"
         :style="data.styleNoteContainer"
         @mousedown.self="addNote"
         @touchstart.self="onTouchStart"
         @touchend.self="onTouchEnd"
    >
        <!-- remark: provide the key binding so that Vue can identify each note component. This is especially important when note is deleted since the
        note length will change. See the Vue documentation for more details-->
        <template v-for="(item,index) in data.noteList" :key="item.id">
            <note :meta="item"
                  :index="index"
                  :pagenum="props.pagenum"
                  :side="props.side"
                  :disabled="toolState.isNoteDisabled"
            ></note>
        </template>
    </div>
</template>

<script>

    import {ref, reactive, onMounted, watchEffect, computed, watch}  from "vue"
    import {usePageResize} from "@/modules/reader/book/pageResize";
    import {usePaging} from "@/modules/reader/book/paging";
    import {useTool} from "@/modules/reader/tools/tools";
    import config from "@/config/config.json";
    import Note from "@/views/reader/tool/note/Note.vue";
    import * as freehandTool from "@/modules/reader/tools/drawing/freehandTool";
    import * as straightLineTool from "@/modules/reader/tools/drawing/straightLineTool";
    import * as arrowTool from "@/modules/reader/tools/drawing/arrowTool";
    import * as eraserTool from "@/modules/reader/tools/drawing/eraserTool";
    import cssvar from '@/scss/_variables.scss';
    import {isTouchDevice} from "@/modules/utils/deviceUtils";
    import {isMultiTouchTouch, replaceDefaultTouchBehavior, restoreDefaultTouchBehavior, setBodyPinchZoomOnly} from "@/modules/utils/touchUtils";

    const zIndexNoteContainer = parseInt(cssvar.zIndexNoteContainer);
    const noteHeight = parseInt(cssvar.noteHeight);
    const noteWidth = parseInt(cssvar.noteWidth);
    const noteMinSize = parseInt(cssvar.noteMinSize);

    export default {
        components: {
            Note
        },
        props: {
            pagenum:Number,
            noteList:Array,
            side:String
        },
        setup: function (props, context) {

            const {pageResizeState} = usePageResize();
            const {toolState, TOOL_NOTE, clearActiveNote} = useTool();
            const {pageState, setPageCount, setCurrentPage, togglePageMode} = usePaging();

            const zIndex = ref(zIndexNoteContainer);

            //get the browser viewport scale when touch just starts, used for detecting is zooming occurs
            let viewPortScaleOnTouchStart = 1;
            //get the browser viewport scale when touch just ends, used for detecting is zooming occurs
            let viewPortScaleOnTouchEnd = 1;
            //determine if zoom gesture occurs by comparing viewPortScaleOnTouchStart and viewPortScaleOnTouchEnd, also count the touch point length together
            let isMultiTouch = false;

            let touchStartX, touchStartY;

            const data = reactive({
                //TODO: move to helper method, don't repeat yourself
                currentZoom: computed(() => {
                    let zoomLevel;
                    if (pageState.isRightPageAvailable) {
                        if (props.side === "left") {
                            zoomLevel = pageResizeState.zoomLevelLeft;
                        } else if (props.side === "right") {
                            zoomLevel = pageResizeState.zoomLevelRight;
                        }
                    } else {
                        zoomLevel = pageResizeState.zoomLevel;
                    }
                    return zoomLevel;
                }),
                /**
                 * Tell whether the note container is ready to receive click/touch event.
                 * This property will be used to determine the note container height.
                 * Setting note container height to zero will prevent adding note when drawing on canvas
                 */
                isActive: computed(() => {

                    return (toolState.activeTool === TOOL_NOTE && toolState.isAllowed) ||
                        (toolState.activeNote !== null && toolState.noteOrdering[props.pagenum] && toolState.noteOrdering[props.pagenum].includes(toolState.activeNote))
                }),
                styleNoteContainer: computed(() => {
                    return {
                        width: `${Math.round(pageResizeState.imgToBrowserScale * pageResizeState.baseWidth)}px`,
                        height: (data.isActive) ? "100%" : 0,
                        zIndex: zIndex.value
                    }
                }),
                /**
                 * Store the physical height (which will be scaled if page is zoomed) of the note container so that the boundary can be checked
                 */
                noteContainerRealHeight: computed(() => {

                    return Math.round(pageResizeState.imgToBrowserScale * pageResizeState.baseHeight * data.currentZoom);
                }),
                /**
                 * Store the physical width (which will be scaled if page is zoomed) of the note container so that the boundary can be checked
                 */
                noteContainerRealWidth: computed(() => {
                    return Math.round(pageResizeState.imgToBrowserScale * pageResizeState.baseWidth * data.currentZoom);
                }),
                noteList: computed(() => {
                    return toolState.noteList[props.pagenum];
                })
            })


            const onTouchStart = (e)=> {
                viewPortScaleOnTouchStart = window.visualViewport.scale;
                touchStartX = e.changedTouches[0].pageX;
                touchStartY = e.changedTouches[0].pageY;
            }

            const onTouchEnd = (e)=> {
                viewPortScaleOnTouchEnd = window.visualViewport.scale;
                addNote(e);
            }

            /**
             * Add note to the container when mousedown event is listened.
             * Remark: click event is not used because the mouse may move out of the note when the note is released after dragging,
             * and the note container will receive the event and add one more note. Use mousedown event to avoid this!
             * @param e
             */
            const addNote = (e) => {

                //e.currentTarget.removeEventListener("touchend", addNote);
                //avoid event propagated to parent div on tablets
                e.stopPropagation();

                //it's detected that e.touches.length is zero if more than one finger, in most cases it will be:
                //first time  e.touches.length=1, e.targetTouches.length=1, e.changedTouches.length=1
                //second time e.touches.length=0, e.targetTouches.length=0, (e.changedTouches.length=1 or e.changedTouches.length=2)
                //if only single touch , then always:
                //e.touches.length=0, e.targetTouches.length=0, e.changedTouches.length=1
                //So check e.touches and isMultiTouch to stop executing the second time if multi-touch occurs, otherwise note will be added
                if (isTouchDevice() && e.changedTouches && e.changedTouches.length) {

                    //do not add note if touch start and end positions are different
                    if (touchStartX !== e.changedTouches[0].pageX || touchStartY !== e.changedTouches[0].pageY) {
                        return;
                    }


                    if (isMultiTouch &&  !e.touches.length) {
                        isMultiTouch = false;
                        return;
                    }

                    if (viewPortScaleOnTouchEnd !== viewPortScaleOnTouchStart || (e.touches.length !== 0 || e.changedTouches.length > 1)) {
                        isMultiTouch = true;
                        //document.getElementById("debug").innerHTML += "is multi <br>"
                        return;
                    } else {
                        isMultiTouch = false
                    }
                }


                if (data.isActive && e.target.className && e.target.className === "note-container" && !isMultiTouch) {

                    document.getElementById("debug").innerHTML += "ready to add note.. <br>"

                    let x, y;
                    let noteContainerBound = e.currentTarget.getBoundingClientRect();
                    let noteContainerLeftBound = noteContainerBound.x;
                    let noteContainerTopBound = noteContainerBound.y;
                    let offsetX, offsetY;
                    let minPosX, minPosY;


                    //TODO: move to helper method, don't repeat yourself
                    if (isTouchDevice() && e.changedTouches && e.changedTouches.length) {
                        offsetX = (e.changedTouches[0].pageX - document.body.scrollLeft -  noteContainerLeftBound) / data.currentZoom
                        offsetY = (e.changedTouches[0].pageY - document.body.scrollTop - noteContainerTopBound) / data.currentZoom
                       // offsetX = e.changedTouches[0].clientX  -  noteContainerLeftBound;
                        //offsetY = e.changedTouches[0].clientY - noteContainerTopBound;
                    } else {
                        offsetX = e.offsetX
                        offsetY = e.offsetY
                    }

                    //Trick to set note container layer order: since hint div z-index can vary, set the note container to the uppermost by adding the hint count
                    //on the current page. This makes sures the noteContainer z-index must be larger than z-index of any hints.
                    //Setting annotation-container div z-index may seem help, but this will block the canvas layer (e.g. the han-pin image will be selected and dragged off when drawing.)
                    //So eventually we end up the following solution. Experiment the z-index of all main layers to see the effect.
                    zIndex.value = zIndexNoteContainer + document.querySelectorAll(`#annotation-container-${props.pagenum} .hint`).length

                    if ((offsetX + noteWidth) * data.currentZoom > data.noteContainerRealWidth) {
                        x = (data.noteContainerRealWidth - noteWidth) / data.currentZoom
                    } else {
                        x = offsetX
                    }

                    if (( offsetY + noteHeight) * data.currentZoom > data.noteContainerRealHeight) {
                        y = (data.noteContainerRealHeight - noteHeight) / data.currentZoom
                    } else {
                        y = offsetY
                    }

                    console.log("@notecontainer add note: " + offsetY + " ; " + y + " ; " + data.noteContainerRealHeight + " ; " + pageResizeState.imgToBrowserScale + " ; " + ((data.noteContainerRealHeight - noteMinSize) / pageResizeState.imgToBrowserScale / data.currentZoom))

                    x = Math.round(x * 100) / 100;
                    y = Math.round(y * 100) / 100;
                    minPosX =  Math.min(offsetX / pageResizeState.imgToBrowserScale, (data.noteContainerRealWidth - noteMinSize) / pageResizeState.imgToBrowserScale / data.currentZoom);
                    minPosY = Math.min(offsetY / pageResizeState.imgToBrowserScale, (data.noteContainerRealHeight - noteMinSize) / pageResizeState.imgToBrowserScale / data.currentZoom);
                    minPosX = Math.round (minPosX * 100) / 100;
                    minPosY = Math.round (minPosY * 100) / 100
                        //if note is cut by boundary, move the note so that the note edge will not exceed the boundary
                    let meta = {
                        x: Math.round(x / pageResizeState.imgToBrowserScale * 100) / 100,
                        y: Math.round(y / pageResizeState.imgToBrowserScale * 100) / 100,
                        minPosX: minPosX,
                        minPosY: minPosY,
                        title: "",
                        content: "",
                    }

                    // console.log("@notecontainer add note: " + meta.x + " ; " + meta.y);
                    useTool().addNote(props.pagenum, meta);
                    useTool().closeToolBoxAndPanel()

                    //allow zooming after note is added
                    restoreDefaultTouchBehavior();
                } else {
                    e.preventDefault();
                    restoreDefaultTouchBehavior();
                    return;
                }
            }

            return {
                data,
                props,
                addNote,
                onTouchStart,
                onTouchEnd,
                toolState,
                zIndex
            }

        }
    }

</script>

<style scoped lang="scss">

    .note-container {
        position:absolute;
        z-index:5;
        width:100%;
        user-select:auto;
    }
</style>